import type { InfiniteData, QueryClient } from "react-query";

import type { GetConnectionRequestsResponse } from "@/react/api/connectionRequests/connectionRequestsApi";
import { PROFILE_PAGES } from "@/react/helpers/memberHelper";
import { CONNECTION_REQUESTS_QUERY_KEY } from "@/react/hooks/connectionRequests/useConnectionRequestsInfiniteQuery";
import { PROFILE_INFO_QUERY_KEY } from "@/react/hooks/profile/useProfileInfo";

const generateInvalidateMemberQueryKey = (memberPublicId: string) => [
  PROFILE_INFO_QUERY_KEY,
  memberPublicId,
  {
    page_name: PROFILE_PAGES.PROFILE_VIEW,
  },
];

/**
 * Invalidates all member-related queries after connection state changes in connections
 * This includes individual member queries and member search results
 */
export const optimisticallyRemoveConnectionRequest = (
  queryClient: QueryClient,
  connectionId: string,
) => {
  const queryKey = [CONNECTION_REQUESTS_QUERY_KEY];

  queryClient.setQueriesData<
    InfiniteData<GetConnectionRequestsResponse> | undefined
  >({ queryKey }, oldData => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      pages: oldData.pages.map(page => ({
        ...page,
        count: Math.max(0, page.count - 1),
        records: page.records.filter(
          record => String(record.id) !== connectionId,
        ),
      })),
    };
  });
};

export const invalidateAllMemberQueries = async (
  queryClient: QueryClient,
  memberPublicId: string,
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["recommendations"],
    }),
    queryClient.invalidateQueries({
      queryKey: generateInvalidateMemberQueryKey(memberPublicId),
    }),
    queryClient.invalidateQueries({
      queryKey: ["community-members"],
    }),
    queryClient.invalidateQueries({
      queryKey: ["audience"],
    }),
    queryClient.invalidateQueries({
      queryKey: [CONNECTION_REQUESTS_QUERY_KEY],
    }),
  ]);
};
